import {
  Box,
  Modal,
  Button,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import countryList from "react-select-country-list";
import CoordinateInput from "./CoordinateInput";

const countryOptions = countryList().getData();

export default function LocationModal({
  open,
  onClose,
  listTypes,
  onSave,
  customerData,
}) {
  const [locationData, setLocationData] = useState({
    location_name: "",
    location_type_id: "",
    location_address: "",
    location_address_2: "",
    location_city: "",
    location_county: "",
    location_postcode: "",
    location_country: "",
    location_coordinates: { lat: "", lon: "" },
    number_of_buildings: 0,
  });
  const [errors, setErrors] = useState({});

  // Check for duplicate location names
  const checkLocationNameExists = (name) => {
    if (!name || !customerData?.locations) return false;
    return customerData.locations.some(
      (loc) => loc.location_name.toLowerCase() === name.toLowerCase(),
    );
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setLocationData({
        location_name: "",
        location_type_id: "",
        location_address: "",
        location_address_2: "",
        location_city: "",
        location_county: "",
        location_postcode: "",
        location_country: "",
        location_coordinates: { lat: "", lon: "" },
        number_of_buildings: 0,
      });
      setErrors({});
    }
  }, [open]);

  const handleChange = (field, value) => {
    setLocationData((prev) => ({ ...prev, [field]: value }));

    if (field === "location_name") {
      if (checkLocationNameExists(value)) {
        setErrors((prev) => ({
          ...prev,
          location_name: "This location name is already in use",
        }));
      } else if (errors.location_name) {
        setErrors((prev) => ({ ...prev, location_name: undefined }));
      }
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCoordsChange = (coords) => {
    setLocationData((prev) => ({ ...prev, location_coordinates: coords }));
  };

  const validate = () => {
    const newErrors = {};
    if (!locationData.location_name)
      newErrors.location_name = "Name is required";
    else if (checkLocationNameExists(locationData.location_name))
      newErrors.location_name = "This location name is already in use";
    if (!locationData.location_type_id)
      newErrors.location_type_id = "Type is required";
    if (!locationData.location_address)
      newErrors.location_address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validate()) {
      try {
        await onSave(locationData);
        onClose(); // Only close if successful
      } catch (err) {
        // Display the error in the modal
        setErrors((prev) => ({
          ...prev,
          general: err.message || "Failed to save location. Please try again.",
        }));
      }
    }
  };

  const isValid =
    locationData.location_name &&
    locationData.location_type_id &&
    locationData.location_address;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          minWidth: 400,
          maxHeight: "80vh",
          overflowY: "auto",
          borderRadius: 2,
        }}
      >
        <TextField
          label="Name"
          fullWidth
          margin="dense"
          value={locationData.location_name}
          onChange={(e) => handleChange("location_name", e.target.value)}
          error={!!errors.location_name}
          helperText={errors.location_name}
        />
        <TextField
          label="Type"
          select
          fullWidth
          margin="dense"
          value={locationData.location_type_id ?? ""}
          onChange={(e) =>
            handleChange("location_type_id", Number(e.target.value) || "")
          }
          error={!!errors.location_type_id}
          helperText={errors.location_type_id}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {listTypes?.map((t) => (
            <MenuItem key={t.pk_id} value={t.pk_id}>
              {t.type_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Address"
          fullWidth
          margin="dense"
          value={locationData.location_address}
          onChange={(e) => handleChange("location_address", e.target.value)}
          error={!!errors.location_address}
          helperText={errors.location_address}
        />
        <TextField
          label="Address 2"
          fullWidth
          margin="dense"
          value={locationData.location_address_2}
          onChange={(e) => handleChange("location_address_2", e.target.value)}
          error={!!errors.location_address_2}
          helperText={errors.location_address_2}
        />
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <TextField
            label="City"
            fullWidth
            margin="dense"
            value={locationData.location_city}
            onChange={(e) => handleChange("location_city", e.target.value)}
            error={!!errors.location_city}
            helperText={errors.location_city}
          />
          <TextField
            label="County"
            fullWidth
            margin="dense"
            value={locationData.location_county}
            onChange={(e) => handleChange("location_county", e.target.value)}
            error={!!errors.location_county}
            helperText={errors.location_county}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <TextField
            label="Postcode"
            fullWidth
            margin="dense"
            value={locationData.location_postcode}
            onChange={(e) => handleChange("location_postcode", e.target.value)}
            error={!!errors.location_postcode}
            helperText={errors.location_postcode}
          />
          <TextField
            label="Country"
            select
            fullWidth
            margin="dense"
            value={locationData.location_country}
            onChange={(e) => handleChange("location_country", e.target.value)}
            error={!!errors.location_country}
            helperText={errors.location_country}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {countryOptions.map((option) => (
              <MenuItem key={option.value} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <CoordinateInput
          value={locationData.location_coordinates}
          onChange={(coords) => handleCoordsChange(coords)}
          error={errors.location_coordinates}
          helperText={errors.location_coordinates}
        />
        {errors.general && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errors.general}
          </Typography>
        )}
        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          {isValid ? (
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
          ) : (
            <Button variant="contained" color="primary" disabled>
              Save
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
